import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface CloudsEffectProps {
  intensity?: number;
  cloudCount?: number;
  areaSize: [number, number];
  color?: string;
  height?: number;
  speed?: number;
  world?: 'real' | 'spectral';
}

interface Cloud {
  position: THREE.Vector3;
  scale: THREE.Vector3;
  rotation: number;
  speed: number;
  opacity: number;
  phase: number;
}

export function CloudsEffect({
  intensity = 0.7,
  cloudCount = 10, // Reduced from 15
  areaSize,
  color,
  height = 8,
  speed = 0.3,
  world = 'real',
}: CloudsEffectProps) {
  const groupRef = useRef<THREE.Group>(null);
  const count = Math.floor(cloudCount * intensity);

  // Determine colors based on world
  const cloudColor = color || (world === 'spectral' ? '#1a1a2e' : '#b8c0c8');
  const baseOpacity = world === 'spectral' ? 0.75 : 0.5;

  // Stabilize areaSize
  const areaSizeKey = `${areaSize[0]}-${areaSize[1]}`;

  // Shared low-poly geometry - created once
  const sphereGeo = useMemo(() => new THREE.SphereGeometry(0.5, 6, 6), []);

  // Create cloud data
  const clouds = useMemo(() => {
    const data: Cloud[] = [];

    for (let i = 0; i < count; i++) {
      const scaleX = 3 + Math.random() * 4;
      const scaleY = 0.4 + Math.random() * 0.4;
      const scaleZ = 2 + Math.random() * 3;

      data.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * areaSize[0] * 1.5,
          height + Math.random() * 3 - 1.5,
          (Math.random() - 0.5) * areaSize[1] * 1.5
        ),
        scale: new THREE.Vector3(scaleX, scaleY, scaleZ),
        rotation: Math.random() * Math.PI * 2,
        speed: speed * (0.5 + Math.random() * 1),
        opacity: baseOpacity * (0.7 + Math.random() * 0.3),
        phase: Math.random() * Math.PI * 2,
      });
    }

    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, areaSizeKey, height, speed, baseOpacity]);

  // Animation - simplified, no material updates per frame
  useFrame((state) => {
    if (!groupRef.current) return;

    const time = state.clock.elapsedTime;

    groupRef.current.children.forEach((child, i) => {
      if (i >= clouds.length) return;
      const cloud = clouds[i];
      const group = child as THREE.Group;

      // Slow drift movement
      const baseX = cloud.position.x + time * cloud.speed;
      const wrappedX = ((baseX + areaSize[0]) % (areaSize[0] * 2)) - areaSize[0];

      group.position.x = wrappedX;
      group.position.y = cloud.position.y + Math.sin(time * 0.2 + cloud.phase) * 0.3;
      group.position.z = cloud.position.z + Math.sin(time * 0.15 + cloud.phase * 0.7) * 0.2;

      // Subtle rotation
      group.rotation.y = cloud.rotation + time * 0.02;
    });
  });

  return (
    <group ref={groupRef}>
      {clouds.map((cloud, i) => (
        <group
          key={`cloud-${i}`}
          position={cloud.position}
          rotation={[0, cloud.rotation, 0]}
        >
          {/* Main cloud body - single low poly sphere, BasicMaterial for performance */}
          <mesh scale={[cloud.scale.x, cloud.scale.y, cloud.scale.z]} geometry={sphereGeo}>
            <meshBasicMaterial
              color={cloudColor}
              transparent
              opacity={cloud.opacity}
              depthWrite={false}
            />
          </mesh>

          {/* One secondary puff for volume */}
          <mesh
            position={[-cloud.scale.x * 0.25, 0, cloud.scale.z * 0.15]}
            scale={[cloud.scale.x * 0.6, cloud.scale.y * 0.9, cloud.scale.z * 0.7]}
            geometry={sphereGeo}
          >
            <meshBasicMaterial
              color={cloudColor}
              transparent
              opacity={cloud.opacity * 0.8}
              depthWrite={false}
            />
          </mesh>

          {/* Dark underbelly for spectral only */}
          {world === 'spectral' && (
            <mesh
              position={[0, -cloud.scale.y * 0.3, 0]}
              scale={[cloud.scale.x * 0.7, cloud.scale.y * 0.3, cloud.scale.z * 0.7]}
              geometry={sphereGeo}
            >
              <meshBasicMaterial
                color="#0a0a15"
                transparent
                opacity={cloud.opacity * 0.6}
                depthWrite={false}
              />
            </mesh>
          )}
        </group>
      ))}
    </group>
  );
}
